import type { LegalDocumentKind } from '@goodbuild/rules/contracts';
import { Empty, Page } from '../ui';
import { Markdown } from '../ui/components/markdown';
import { useLegalDocument } from '../site-config';
import { column, Loading } from './shared';
import { useDocumentTitle } from '../title';

/**
 * A legal document, at a measure somebody can read.
 *
 * The three legal pages are one component with a kind passed in, so the three
 * cannot drift apart in how they are presented — which is how one of them ends
 * up with a different measure, a different version label, or a draft notice the
 * others do not have.
 *
 * **The text is the same source the acceptance flow hashes.** A legal page that
 * renders different words from the ones somebody accepted is worse than no
 * page, so both come from `LEGAL_DOCUMENTS` rather than one being a copy. It
 * arrives over `GET /v1/site/legal/:kind` rather than being bundled, for a
 * reason beyond weight: `lib/legal/documents` imports `node:crypto` for
 * `hashDocument`, and a browser build has no answer for that.
 *
 * **The body is Markdown and `Markdown` renders it.** It used to be printed as
 * preformatted source, with `#` and `**` visible and every clause broken at the
 * column the author typed to, and the note here said that was a real defect
 * left for later. Later is now: the renderer covers the whole vocabulary these
 * documents use, and the same component draws the copy somebody accepts inside
 * the onboarding flow, so the read and the agreement cannot render differently.
 *
 * That this was safe to change at all is the arrangement working as designed.
 * `pnpm legal:sync` hashes the source rather than the rendering, so how a
 * document is displayed can move without invalidating a single acceptance.
 */
export function LegalRoute({
  kind,
  fallbackTitle,
}: {
  kind: LegalDocumentKind;
  /** Shown when nothing of this kind is written yet. */
  fallbackTitle: string;
}): React.ReactElement {
  const query = useLegalDocument(kind);
  const document = query.data?.document ?? null;
  useDocumentTitle(document?.title ?? fallbackTitle);
  const draft = document === null || document.status !== 'published';

  return (
    <Page site="store">
      <header style={column(4)}>
        <p className="gb-eyebrow">
          {document ? `Version ${document.version}` : 'Not yet published'}
        </p>
        <h1 className="gb-headline">{document?.title ?? fallbackTitle}</h1>
        {draft && document ? (
          <p className="gb-lead">
            A draft. It has not been reviewed by a lawyer, it is not in force, and nobody can accept
            it. It is here so you can read what will be asked before it is finalised.
          </p>
        ) : null}
      </header>

      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <Empty>{query.error.message}</Empty>
      ) : document ? (
        <article>
          <Markdown source={document.body} />
        </article>
      ) : (
        <p className="gb-body">Not yet published. It will appear here the moment it is.</p>
      )}
    </Page>
  );
}
